/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/no-array-index-key */
import { useMediaQuery } from "beautiful-react-hooks";
import React, { useEffect, useRef, useState } from "react";
import { Trans } from "@lingui/macro";
import { Icon } from "../Icon";
import { isSSR } from "../NoSSR";
import PopupOrDropdownAdapter from "../PopupOrDropdownAdapter";
import Spinner from "../Spinner";
import "./DropdownInput.css";
import sendEvent from "../../functions/analytics";
import Button from "../Button";

export const DropdownInput = ({
  id,
  label,
  value,
  options,
  onSelect,
  activeDropdownId,
  onToggleDropdown,
  disabledOption,
  icon,
  search = false,
  enablePlayer = false,
  closeButton = false,
}) => {
  const isMobile = !isSSR && useMediaQuery("(max-width: 767px)");

  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchQuery, setSearchQuery] = useState("");
  const [playingSample, setPlayingSample] = useState(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (options?.length && value) {
      const optionMatch = options.find(option => option.value === value);
      setSearchQuery(optionMatch?.label || "");

      return () => setSearchQuery("");
    }
  }, [options, value]);

  const handleSearchChange = event => {
    const query = event.target.value;
    setSearchQuery(query);
    setFilteredOptions(
      options.filter(option => option.label.toLowerCase().includes(query.toLowerCase())),
    );
  };

  const toggleSamplePlayback = sampleUrl => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingSample === sampleUrl) {
      setPlayingSample(null);
    } else {
      const newAudio = new Audio(sampleUrl);
      audioRef.current = newAudio;
      setPlayingSample(sampleUrl);
      newAudio.play();
      newAudio.onended = () => {
        setPlayingSample(null);
        audioRef.current = null;
      };
    }
  };

  useEffect(() => {
    if (activeDropdownId !== id) {
      setShowDropdown(false);
    }
  }, [activeDropdownId, id]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleSelect = option => {
    if (option !== disabledOption) {
      onSelect(option);
      if (!isMobile) {
        setShowDropdown(false);
        onToggleDropdown(null);
      }
      setSearchQuery(option.label || "");
    }
  };

  const renderDropdownItems = () => {
    return filteredOptions.map((option, index) => {
      const isChecked = value === option.value;
      return (
        <li
          key={index}
          className={`DropdownInput__listItem ${option === disabledOption ? "disabled" : ""}`}
          style={{
            cursor: option === disabledOption ? "not-allowed" : "pointer",
            color: option === disabledOption ? "gray" : "black",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          onMouseDown={() => {
            if (option !== disabledOption) {
              handleSelect(option);
            }
          }}
        >
          <div className="DropdownInput__checkbox_block">
            {isMobile && (
              <input
                type="checkbox"
                id={`checkbox-${index}`}
                className="DropdownInput__checkbox"
                checked={isChecked}
                onChange={() => handleSelect(option)}
              />
            )}
            <span>{option.label}</span>
          </div>
          {enablePlayer && option.sample && (
            <button
              className="DropdownInput__playerButton"
              onMouseDown={e => {
                e.stopPropagation();
                toggleSamplePlayback(option.sample);
                sendEvent("track", "tour_params_play_sample");
              }}
            >
              {playingSample === option.sample ? (
                <svg viewBox="0 -3 24 24" width="24" height="24">
                  <path
                    fill="#000"
                    fillOpacity=".98"
                    d="M14.5 18V6h3.075v12H14.5Zm-8.075 0V6H9.5v12H6.425Z"
                  />
                </svg>
              ) : (
                <svg viewBox="0 -5 8 18" width="18" height="18">
                  <title>Play</title>
                  <path fill="#000" fillOpacity=".98" d="M10,6.63L0,13.25V0L10,6.63z" />
                </svg>
              )}
            </button>
          )}
        </li>
      );
    });
  };

  const inputContainer = (
    <div
      className="DropdownInput__inputContainer"
      onClick={e => {
        if (!disabledOption && !e.target.closest(".DropdownInput__playerButton")) {
          const newState = !showDropdown;
          setShowDropdown(newState);
          onToggleDropdown(newState ? id : null);
        }
      }}
    >
      <Icon className="DropdownInput__icon" name={icon} />
      <input
        readOnly={!search}
        type={search ? "search" : "text"}
        value={searchQuery}
        placeholder=""
        disabled={disabledOption}
        className={`DropdownInput__input ${value ? "DropdownInput__input_active" : ""}`}
        onChange={search ? handleSearchChange : undefined}
      />
      <label className="DropdownInput__label">{label}</label>
      <div className="DropdownInput__chevron">
        <Icon name={`chevron/${showDropdown ? "up" : "down"}`} width="24" height="24" />
      </div>
    </div>
  );

  const dropdownContent = (
    <div className="DropdownInput__popup">
      {options.length === 0 ? (
        <div className="DropdownInput__popup_spinner">
          <Spinner />
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0 }}>{renderDropdownItems()}</ul>
      )}
    </div>
  );

  return (
    <div ref={inputRef} style={{ position: "relative" }}>
      {!isMobile ? (
        <>
          {inputContainer}
          {showDropdown && dropdownContent}
        </>
      ) : (
        <PopupOrDropdownAdapter
          title={label}
          ContentComponent={() => (
            <div className="ActivitiesSort__radioMenu_items">
              <ul style={{ listStyle: "none", padding: 0, marginBottom: 10 }}>
                {renderDropdownItems()}
              </ul>
              {closeButton && (
                <Button theme="black" size="L" onClick={() => setShowDropdown(false)}>
                  <Trans>Save</Trans>
                </Button>
              )}
            </div>
          )}
          isMobile={isMobile}
          triggerElement={inputContainer}
          open={showDropdown}
          onClose={() => setShowDropdown(false)}
          onOpened={() => setShowDropdown(true)}
          onClosed={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};
